import { safeLocalGet, safeLocalRemove, safeLocalSet } from "@/lib/browser-storage";

export const ACCOUNT_TOKEN_KEY = "ishvaram:account:token";
const ACCOUNT_SOURCE_KEY = "ishvaram:account:source";

export type AccountSessionSource = "auth" | "contact";

export interface AccountSession {
  token: string;
  source: AccountSessionSource;
}

let snapshot: AccountSession | null | undefined;
const listeners = new Set<() => void>();

function readStoredSession(): AccountSession | null {
  const token = safeLocalGet(ACCOUNT_TOKEN_KEY)?.trim();
  if (!token) return null;
  const source = safeLocalGet(ACCOUNT_SOURCE_KEY) === "auth" ? "auth" : "contact";
  return { token, source };
}

function emit(): void {
  listeners.forEach((listener) => listener());
}

export function getAccountSessionSnapshot(): AccountSession | null {
  if (snapshot === undefined) snapshot = readStoredSession();
  return snapshot;
}

export function storeAccountSession(token: string, source: AccountSessionSource): void {
  const normalized = token.trim();
  if (!normalized) return;
  safeLocalSet(ACCOUNT_TOKEN_KEY, normalized);
  safeLocalSet(ACCOUNT_SOURCE_KEY, source);
  snapshot = { token: normalized, source };
  emit();
}

export function clearAccountSession(): void {
  safeLocalRemove(ACCOUNT_TOKEN_KEY);
  safeLocalRemove(ACCOUNT_SOURCE_KEY);
  snapshot = null;
  emit();
}

export function subscribeAccountSession(listener: () => void): () => void {
  listeners.add(listener);
  const handleStorage = (event: StorageEvent) => {
    if (event.key !== ACCOUNT_TOKEN_KEY && event.key !== ACCOUNT_SOURCE_KEY) return;
    snapshot = readStoredSession();
    emit();
  };
  window.addEventListener("storage", handleStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", handleStorage);
  };
}
